import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import type { LucideIcon } from 'lucide-react'
import { cn } from './cn'

/**
 * Button — consumes design CSS (.btn .{variant} .{size}) and applies variants via cva.
 *
 * Variants map to the Night Ops design:
 *   primary  — accent-filled, the headline CTA
 *   ghost    — text-only, for toolbar actions
 *   danger   — bad-tone filled, destructive ops
 *   outline  — bordered, secondary actions
 *   subtle   — filled panel-2, neutral actions
 *
 * Scaling: add a new variant by adding a class to styles.css (e.g. `.btn.success`)
 * and extending the `variant` enum below. No breaking changes to callers.
 */
const buttonStyles = cva('btn', {
  variants: {
    variant: {
      primary: 'primary',
      ghost: 'ghost',
      danger: 'danger',
      outline: 'outline',
      subtle: 'subtle',
      /** Positive/confirmation CTA — solid green fill. Used for Partner Club grant. */
      success: 'success',
      default: '',
    },
    size: {
      sm: 'sm',
      md: '',
      lg: 'lg',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonStyles> & {
    icon?: LucideIcon | undefined
    /** Show a spinning icon + disable the button. Signals the button is
     * working (force-update, save, deploy-style action). The caller can
     * still override `disabled` to false if needed. */
    loading?: boolean | undefined
    /** Progress-tense label shown in place of `children` while `loading`.
     * Gives the operator positive confirmation the tap registered —
     * "Cobrando…" instead of a still "Cobrar". When omitted, `children`
     * stays visible (icon still spins), preserving the prior behaviour. */
    loadingText?: ReactNode | undefined
    children?: ReactNode | undefined
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, icon, loading, loadingText, children, className, type = 'button', disabled, ...rest }, ref) => {
    const IconComp = icon
    const label = loading && loadingText != null ? loadingText : children
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonStyles({ variant, size }), loading && 'btn--loading', className)}
        {...rest}
      >
        {IconComp && <IconComp size={14} />}
        {label && <span>{label}</span>}
      </button>
    )
  },
)
Button.displayName = 'Button'
